import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { inNextServer, getDefaultRadixPortalContainer } from '@fabric-app/compat';
import { useFloating, autoUpdate, offset, flip, shift, arrow, useInteractions, useHover, useFocus, useRole, useDismiss, safePolygon } from '@floating-ui/react-dom-interactions';
import { useState, useRef, useMemo, createContext, cloneElement, useContext } from 'react';
import { mergeRefs } from 'react-merge-refs';
import { styled } from 'styled-components';
import { Root } from '@radix-ui/react-portal';
import { mediaMobile } from '../constants/styled-utils.js';
import { cssVar } from '../theme/variables.js';
import { IconInfoTabItem } from '@fabric-app/icons/old';

const placementRotationMap = {
  top: "45deg",
  right: "135deg",
  bottom: "-135deg",
  left: "-45deg"
};
const TooltipContext = /* @__PURE__ */ createContext({
  open: false,
  setOpen: () => {
  }
});
const TooltipArrow = styled.span.withConfig({
  displayName: "Tooltip__TooltipArrow",
  componentId: "sc-f568afd3-0"
})([
  `position:absolute;width:10px;height:10px;background-color:black;`
]);
const TooltipBox = styled.span.withConfig({
  displayName: "Tooltip__TooltipBox",
  componentId: "sc-f568afd3-1"
})([
  `padding:9px 10px;font-size:15px;color:white;background-color:black;border-radius:10px;width:max-content;z-index:300;`,
  `{display:none;}&[data-error='true']{background-color:#b02828;`,
  `{background-color:#b02828;}}`
], mediaMobile, TooltipArrow);
const useInsideTooltip = () => useContext(TooltipContext);
const defaultLabelStyles = {};
const Tooltip = ({ children, placement = "bottom", label, portal = true, maxSize = {
  width: "50vw",
  height: "50vh"
}, forceShow = false, error = false, delay = 1e3, labelStyles = defaultLabelStyles, allowTooltipHover = false, disabled = false, offset: offsetProp = 20 }) => {
  const [open, setOpen] = useState(false);
  const arrowRef = useRef(null);
  const { x, y, reference, floating, strategy, context, middlewareData, placement: realPlacement } = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    middleware: [
      offset(offsetProp),
      flip(),
      shift({
        padding: 8
      }),
      arrow({
        element: arrowRef
      })
    ],
    whileElementsMounted: autoUpdate
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, {
      delay: {
        open: delay,
        close: 0
      },
      handleClose: allowTooltipHover ? safePolygon() : void 0
    }),
    useFocus(context),
    useRole(context, {
      role: "tooltip"
    }),
    useDismiss(context)
  ]);
  const ref = useMemo(() => {
    if (!("ref" in children)) {
      console.warn("Tooltip: children has no ref, the tooltip might not work as expected.", void 0);
      return reference;
    }
    return mergeRefs([
      reference,
      children.ref
    ]);
  }, [
    reference,
    children
  ]);
  if (disabled) {
    return children;
  }
  const staticSide = {
    top: "bottom",
    right: "left",
    bottom: "top",
    left: "right"
  }[realPlacement.split("-")[0]];
  const arrowDeg = placementRotationMap[staticSide ?? "top"];
  const tooltip = /* @__PURE__ */ jsx(TooltipContext.Provider, {
    value: {
      open,
      setOpen
    },
    children: (open || forceShow) && label && /* @__PURE__ */ jsxs(TooltipBox, {
      ...getFloatingProps({
        ref: floating,
        style: {
          position: strategy,
          top: y ?? 0,
          left: x ?? 0,
          minWidth: 32,
          maxWidth: maxSize.width,
          maxHeight: maxSize.height,
          ...labelStyles
        }
      }),
      "data-error": error,
      children: [
        label,
        /* @__PURE__ */ jsx(TooltipArrow, {
          ref: arrowRef,
          style: {
            left: middlewareData.arrow?.x ?? "auto",
            top: middlewareData.arrow?.y ?? "auto",
            [staticSide ?? "top"]: "-5px",
            transform: `rotate(${arrowDeg})`
          }
        })
      ]
    })
  });
  if (inNextServer()) return children;
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      /* @__PURE__ */ cloneElement(children, getReferenceProps({
        ref,
        ...children.props
      })),
      portal ? /* @__PURE__ */ jsx(Root, {
        container: getDefaultRadixPortalContainer(),
        children: tooltip
      }) : tooltip
    ]
  });
};
const Info = styled.div.attrs({
  children: /* @__PURE__ */ jsx(IconInfoTabItem, {})
}).withConfig({
  displayName: "Tooltip__Info",
  componentId: "sc-f568afd3-2"
})([
  `height:1rem !important;width:1rem !important;display:flex;align-items:center;justify-content:center;font-size:1.25rem;color:`,
  `;flex-grow:0;flex-shrink:0;`
], cssVar["color-text-tertiary"]);
const Tooltip$1 = Object.assign(Tooltip, {
  Info
});

export { Tooltip$1 as default, useInsideTooltip };
//# sourceMappingURL=Tooltip.js.map
